import { type FlowInputSchema, type FlowInputSchemaProperty } from 'flow-schema';
import { sortBy } from 'lodash';
import * as uuid from 'uuid';
import { z } from 'zod';

import { api, isDefined } from 'common';

const ReactFlowInputParameterConfigSchema = z.object({
  id: z.string(),
  name: api.FlowDefinition.IdentifierNameSchema, // must be a valid identifier
  description: z.string(),
});
export type ReactFlowInputParameterConfig = z.infer<typeof ReactFlowInputParameterConfigSchema>;

export const ReactFlowInputParametersConfigSchema = z
  .array(ReactFlowInputParameterConfigSchema)
  .superRefine((parameters, ctx) => {
    // names must be unique
    const nameIndexes = new Map<string, number>();
    const duplicateIndexes = new Set<number>();
    parameters.forEach(({ name }, i) => {
      if (name) {
        const priorIndex = nameIndexes.get(name);
        if (isDefined(priorIndex)) {
          duplicateIndexes.add(i);
          duplicateIndexes.add(priorIndex);
        }
        nameIndexes.set(name, i);
      }
    });
    for (const i of duplicateIndexes) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Name is duplicated',
        path: [i, 'name'],
      });
    }
  });
export type ReactFlowInputParametersConfig = z.infer<typeof ReactFlowInputParametersConfigSchema>;

export function createParameterConfig(): ReactFlowInputParameterConfig {
  return {
    id: uuid.v7(),
    name: '',
    description: '',
  };
}

export function convertParametersConfigToInputSchema(
  parameters: ReactFlowInputParameterConfig[] | undefined
): FlowInputSchema | undefined {
  if (!parameters?.length) {
    return undefined;
  }
  return {
    properties: Object.fromEntries<FlowInputSchemaProperty>(
      parameters.map(({ id, name, description }) => [name, { id, description }])
    ),
  };
}

export function convertInputSchemaToParametersConfig(
  inputSchema: FlowInputSchema | undefined
): ReactFlowInputParameterConfig[] | undefined {
  if (!isDefined(inputSchema?.properties)) {
    return undefined;
  }
  return sortBy(
    Object.entries(inputSchema.properties).map<ReactFlowInputParameterConfig>(
      ([name, { id, description }]) => ({
        id,
        name,
        description,
      })
    ),
    ({ id }) => id
  );
}

interface MergeParamsConfigOptions {
  existingParameters: ReactFlowInputParameterConfig[];
  newParameters: ReactFlowInputParameterConfig[];
}

/**
 * Merges two parameter configs, preserving existing parameter IDs where names match.
 * Also preserves existing parameters that aren't in the new parameters list.
 */
export function mergeInputParametersConfig(
  props: MergeParamsConfigOptions
): ReactFlowInputParameterConfig[] {
  const { existingParameters, newParameters } = props;
  const existingParametersByName = new Map(existingParameters.map((param) => [param.name, param]));
  const newParametersByName = new Map(newParameters.map((param) => [param.name, param]));

  const mergedParameters: ReactFlowInputParameterConfig[] = [];
  for (const [existingParameterNames, existingParameter] of existingParametersByName.entries()) {
    const newParameter = newParametersByName.get(existingParameterNames);
    if (isDefined(newParameter)) {
      mergedParameters.push({
        ...existingParameter,
        ...newParameter,
        id: existingParameter.id, // Preserve existing ID
      });
    } else {
      mergedParameters.push(existingParameter);
    }
  }

  for (const [newParameterName, newParameter] of newParametersByName.entries()) {
    if (!existingParametersByName.has(newParameterName)) {
      mergedParameters.push({
        ...newParameter,
        id: uuid.v7(),
      });
    }
  }

  return mergedParameters;
}
